import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { LabelSection, LabelToggle, MasterSourceSelect, Section, Separator, ToggleCard } from '@Components';
import { ConfigAuditAlerts } from '@Models';

interface AuditAlertSectionProps {
  data: ConfigAuditAlerts;
  readOnly?: boolean;
}

const AuditAlertSection = ({ data, readOnly = true }: AuditAlertSectionProps) => {
  const { t } = useTranslation();

  const auditScopeOptions = [
    {
      value: 'AllAlerts',
      label: t('ConfigurationPolicy.OSA.AllAlerts'),
      description: t('ConfigurationPolicy.OSA.AllAlertsDesc'),
    },
    {
      value: 'Provided',
      label: t('ConfigurationPolicy.OSA.Provided'),
      description: t('ConfigurationPolicy.OSA.ProvidedDesc'),
    },
  ];

  const prioritizationOptions = [
    {
      value: 'AllAlertsInScope',
      label: t('ConfigurationPolicy.OSA.AllAlertsInScope'),
    },
    {
      value: 'TopOpportunityAlerts',
      label: t('ConfigurationPolicy.OSA.TopOpportunityAlerts'),
    },
    {
      value: 'TopSellerAlerts',
      label: t('ConfigurationPolicy.OSA.TopSellerAlerts'),
    },
  ];

  const frequencyOptions = [
    {
      value: 'Daily',
      label: t('ConfigurationPolicy.OSA.Daily'),
    },
    {
      value: 'Weekly',
      label: t('ConfigurationPolicy.OSA.Weekly'),
    },
    {
      value: 'Custom',
      label: t('ConfigurationPolicy.OSA.Custom'),
    },
  ];

  const deliveryOptions = [
    {
      value: 'MerchandiserIntegration',
      label: t('ConfigurationPolicy.OSA.MerchandiserIntegration'),
    },
    {
      value: 'FieldOpsIntegration',
      label: t('ConfigurationPolicy.OSA.FieldOpsIntegration'),
    },
    {
      value: 'WhatsAppChatbot',
      label: t('ConfigurationPolicy.OSA.WhatsAppChatbot'),
    },
  ];

  return (
    <div className="space-y-6 mt-10">
      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.OSA.AuditAlertScope')}
            description={t('ConfigurationPolicy.OSA.AuditAlertScopeDesc')}
          />
        }
        right={
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {auditScopeOptions.map(({ label, description, value }) => {
                const isDataSelected = value === data?.AuditAlertScope?.Selection;
                return (
                  <ToggleCard key={value} selected={isDataSelected} readOnly={readOnly}>
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-2">
                        <LabelToggle label={label} />
                      </div>
                      <p className={cn('text-sm', isDataSelected ? 'text-brand-500' : 'text-gray-700')}>
                        {description}
                      </p>
                    </div>
                  </ToggleCard>
                );
              })}
            </div>
            {data?.AuditAlertScope?.Master && (
              <div className="mt-9">
                <MasterSourceSelect
                  value={{
                    MasterSource: data.AuditAlertScope.Master.Source,
                    MasterColumnName: data.AuditAlertScope.Master.MasterColumnName,
                  }}
                  onChange={() => {}}
                  readOnly={readOnly}
                />
              </div>
            )}
          </>
        }
      />
      <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />
      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.OSA.AuditAlertPrioritization')}
            description={t('ConfigurationPolicy.OSA.AuditAlertPrioritizationDesc')}
          />
        }
        right={
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {prioritizationOptions.map(({ label, value }) => {
                const isDataSelected = value === data.AuditAlertPrioritization.Selection;
                return (
                  <ToggleCard key={value} selected={isDataSelected} readOnly={readOnly}>
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-2">
                        <LabelToggle label={label} />
                      </div>
                    </div>
                  </ToggleCard>
                );
              })}
            </div>
          </div>
        }
      />
      <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />
      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.OSA.AuditAlertFrequency')}
            description={t('ConfigurationPolicy.OSA.AuditAlertFrequencyDesc')}
          />
        }
        right={
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {frequencyOptions.map(({ label, value }) => {
                const isDataSelected = value === data.AuditAlertFrequency.Selection;
                return (
                  <ToggleCard key={value} selected={isDataSelected} readOnly={readOnly}>
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-2">
                        <LabelToggle label={label} />
                      </div>
                    </div>
                  </ToggleCard>
                );
              })}
            </div>
          </div>
        }
      />
      <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />
      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.OSA.AuditAlertDelivery')}
            description={t('ConfigurationPolicy.OSA.AuditAlertDeliveryDesc')}
          />
        }
        right={
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {deliveryOptions.map(({ label, value }) => {
                const isDataSelected = value === data.AuditAlertDelivery.Selection;
                return (
                  <ToggleCard key={value} selected={isDataSelected} readOnly={readOnly}>
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-2">
                        <LabelToggle label={label} />
                      </div>
                    </div>
                  </ToggleCard>
                );
              })}
            </div>
          </div>
        }
      />
    </div>
  );
};

export default AuditAlertSection;
